import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import {
  BILLING_CYCLE_STATUSES,
  FUNDING_SOURCES,
  FUNDING_SOURCE_LABELS,
  type BillingCycleStatus,
  type FundingSource,
} from "@/types/billing-cycle";
import { createTableFiltersStore } from "./createTableFiltersStore";

/**
 * Sentinel for "no status filter".
 *
 * Radix Select throws on a `<SelectItem value="">` and treats `""` as "nothing selected", so the
 * cleared state needs a real value. No BillingCycleStatus member is `'All'`, so this cannot collide
 * with a genuine status.
 */
export const ALL_STATUSES = "All";

export const BILLING_CYCLE_STATUS_OPTIONS: {
  value: BillingCycleStatus | typeof ALL_STATUSES;
  label: string;
}[] = [
  { value: ALL_STATUSES, label: "All statuses" },
  ...BILLING_CYCLE_STATUSES.map((status) => ({ value: status, label: status })),
];

/** Sentinel for "no funding filter" — same reason as `ALL_STATUSES`. */
export const ALL_FUNDING_SOURCES = "All";

/** Labelled the way the Funding column reads ("Paid" / "Trial"); the value is the enum name. */
export const FUNDING_SOURCE_OPTIONS: {
  value: FundingSource | typeof ALL_FUNDING_SOURCES;
  label: string;
}[] = [
  { value: ALL_FUNDING_SOURCES, label: "All funding" },
  ...FUNDING_SOURCES.map((source) => ({
    value: source,
    label: FUNDING_SOURCE_LABELS[source],
  })),
];

export interface BillingCycleListFilters extends Record<string, unknown> {
  /**
   * The one search term, routed by SHAPE in `AdminBillingCycleRepository` — the same rule
   * `AdminResourceRepository` applies, which is the reference for this box:
   *
   * - a whole GUID matches by equality against the cycle id, the resource id, the tenant id and
   *   the provider's VM UUID;
   * - a whole e-mail matches the owner by equality;
   * - anything else is a case-insensitive substring over the VM name, the provider UUID and the
   *   owner's e-mail, plus a phone arm that folds both `+84…` and `0…` to the subscriber digits.
   *
   * `tenantId` used to be a separate field with its own input. It was retired because the GUID
   * branch above already answers it: pasting a tenant id here returns that tenant's cycles, and a
   * second box asked the operator to know which kind of id they were holding before they could
   * search for it. Verified on dev across all 23 tenants that have cycles — the two paths return
   * the same count for every one of them.
   */
  search: string;

  /** Cycle status, or `All`. Sent as the backend's own enum member name. */
  status: BillingCycleStatus | typeof ALL_STATUSES;

  /** What funded the cycle, or `All`. Sent as the backend's own enum member name. */
  fundingSource: FundingSource | typeof ALL_FUNDING_SOURCES;

  /** Created-at range, sent as createdFrom / createdTo. Both ends or neither. */
  createdRange: DateRangeValue;
}

/**
 * True when a stored range has exactly one end. The picker only ever produces both or neither, so
 * this can only come from hand-edited storage.
 */
const isHalfRange = (range: DateRangeValue | undefined): boolean =>
  Boolean(range?.from) !== Boolean(range?.to);

/**
 * The created-at range opens on the last six months, the same bounded window the tenant and
 * resource lists use. Computed once per page load, so a tab left open for days keeps the window it
 * started with until the filters are reset.
 *
 * It does mean a cycle created earlier is not in the first page an operator sees — the chip in the
 * bar names the window, and clearing it searches all time.
 */
const defaults: BillingCycleListFilters = {
  search: "",
  status: ALL_STATUSES,
  fundingSource: ALL_FUNDING_SOURCES,
  createdRange: lastMonthsRange(),
};

/**
 * Filter, sort and paging state for the admin billing-cycle list, cached in sessionStorage.
 *
 * Bump `version` in step with any change to the shape above or to the sortable columns — a cached
 * value the backend no longer accepts comes back as a 400 on a filter the operator cannot see.
 */
export const useBillingCycleFiltersStore =
  createTableFiltersStore<BillingCycleListFilters>({
    name: "admin.billing-cycles.filters",
    // 4: `tenantId` retired into `search` (see the field doc above).
    //
    // What the bump actually buys here is narrower than the usual case, and worth being exact
    // about. This page names its query params one by one rather than spreading the filters object,
    // so a stale stored `tenantId` was never sent, and `hasActiveFilters` only walks the keys of
    // `defaults`, so it was never counted either — measured by loading a hand-written v3 entry
    // carrying an id: no `tenantId` reached the request and no chip appeared. What it did do was
    // sit in every operator's localStorage forever, because `sanitize` spreads what it is given.
    // The bump is what rewrites the entry without it.
    //
    // The general rule still holds and is why this is not skipped: a screen that spreads its
    // filters into the query - which is the more common shape - would have gone on filtering by an
    // id with no control left on screen to clear it.
    //
    // `fundingSource` was added without a bump: `restoreFilters` gives a stored entry that lacks a
    // key its default, so bumping would only have thrown away every operator's saved filters.
    version: 4,
    defaults,
    defaultSort: { by: "CreatedAt", order: "desc" },
    allowedSortFields: [
      "CreatedAt",
      "CycleStart",
      "CycleEnd",
      "NextBillingDate",
      "Amount",
      "Status",
    ],
    sanitize: (filters) => ({
      ...filters,

      // sessionStorage outlives a release: a status that is no longer offered must not survive as a
      // filter the operator cannot see in the UI, and so cannot clear from it.
      status: BILLING_CYCLE_STATUS_OPTIONS.some(
        (option) => option.value === filters.status,
      )
        ? filters.status
        : defaults.status,

      // Same rule for the funding source.
      fundingSource: FUNDING_SOURCE_OPTIONS.some(
        (option) => option.value === filters.fundingSource,
      )
        ? filters.fundingSource
        : defaults.fundingSource,

      // Exactly one end is not a range: the query applies createdFrom/createdTo together, so a
      // stored half would show as a chip that narrows nothing. An *empty* range is left alone — it
      // is the operator having deliberately cleared the six-month default to search all time, and
      // replacing it here would silently put the default back on every reload.
      createdRange: isHalfRange(filters.createdRange)
        ? defaults.createdRange
        : filters.createdRange,
    }),
  });
